import React, { useMemo, useState } from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
// endregion External libraries
// region Atoms
import SearchField from "@atoms/SearchField";
// endregion Atoms
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import HelpTip from "../HelpTip";
import { LegacyMigrationMessages } from "../languages/interfaces";
import { forSource } from "../messages";
import { LegacyMigrationTenant, LegacySource } from "../service";
import * as Styled from "../styles";

/** The sources, in the order they are offered. */
const SOURCES: { source: LegacySource; label: LegacyMigrationMessages }[] = [
  { source: "CRM", label: LegacyMigrationMessages.legacyMigrationSourceCrm },
  { source: "FLEET", label: LegacyMigrationMessages.legacyMigrationSourceFleet }
];

interface IChooseCustomerProps {
  tenants: LegacyMigrationTenant[];
  /** False while the list has never been read; see the empty state below. */
  loaded: boolean;
  busy: boolean;
  /** The picked source can run on this environment (the CRM needs its registry; FLEET needs nothing). */
  configured: boolean;
  source: LegacySource;
  onChangeSource: (source: LegacySource) => void;
  onStart: (tenantId: string) => void;
}

/**
 * Step 1 — where the users come from and which customer to migrate. For the
 * CRM every customer is listed; for FLEET, those with FLEET connected in the
 * Tenant Manager. Running one again is safe, and the review shows who is
 * already in Identity.
 */
const ChooseCustomer: React.FC<IChooseCustomerProps> = ({
  tenants, loaded, busy, configured, source, onChangeSource, onStart
}) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [search, setSearch] = useState<string>("");
  // endregion States

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return tenants;

    return tenants.filter((tenant) => tenant.name.toLowerCase().includes(term)
      || tenant.alias.toLowerCase().includes(term));
  }, [tenants, search]);

  return (
    <Styled.Card>
      <Styled.SourcePicker>
        <Styled.SourceLabel>{t(LegacyMigrationMessages.legacyMigrationSourceLabel)}</Styled.SourceLabel>
        <Styled.Filters role="radiogroup" aria-label={String(t(LegacyMigrationMessages.legacyMigrationSourceLabel))}>
          {SOURCES.map((option) => (
            <Styled.FilterChip
              key={option.source}
              type="button"
              role="radio"
              aria-checked={source === option.source}
              $active={source === option.source}
              disabled={busy}
              onClick={() => onChangeSource(option.source)}
            >
              {t(option.label)}
            </Styled.FilterChip>
          ))}
        </Styled.Filters>
      </Styled.SourcePicker>

      <Styled.StepTitle>{t(LegacyMigrationMessages.legacyMigrationChooseTitle)}</Styled.StepTitle>
      {loaded && (
        <Styled.StepHint>
          {t(forSource(source, LegacyMigrationMessages.legacyMigrationChooseHint), { count: tenants.length })}
        </Styled.StepHint>
      )}

      {/* Always, whatever the source and the size of the list: the same screen every time. */}
      <Styled.Toolbar>
        <Styled.SearchBox>
          <SearchField
            fieldLabel={t(LegacyMigrationMessages.legacyMigrationSearchTenant)}
            onSearchClick={setSearch}
            onClearClick={() => setSearch("")}
          />
        </Styled.SearchBox>
      </Styled.Toolbar>

      {/* An empty list because the request failed must not read as "no
          customers": the banner above says what went wrong. */}
      {visible.length === 0 && (
        <Styled.Empty>
          {t(loaded
            ? LegacyMigrationMessages.legacyMigrationNoTenants
            : LegacyMigrationMessages.legacyMigrationTenantsUnavailable)}
        </Styled.Empty>
      )}

      <Styled.TenantList>
        {visible.map((tenant) => (
          <Styled.TenantRow key={tenant.id}>
            <Styled.TenantName>
              <strong>
                {tenant.name}
                {!tenant.active && ` · ${t(LegacyMigrationMessages.legacyMigrationInactiveTenant)}`}
              </strong>
              <span>{tenant.alias}</span>
              {/* Shown, not hidden: the review still opens, and says who would get access once it is contracted. */}
              {source === "FLEET" && tenant.fleetContracted === false && (
                <Styled.WithHelp>
                  <Styled.Badge $tone="new">{t(LegacyMigrationMessages.legacyMigrationFleetNotContracted)}</Styled.Badge>
                  <HelpTip text={String(t(LegacyMigrationMessages.legacyMigrationFleetNotContractedHint))} />
                </Styled.WithHelp>
              )}
            </Styled.TenantName>

            {/* One per row, so it stays light: the list is the choice. */}
            <Button variant="outlined" disabled={busy || !configured} onClick={() => onStart(tenant.id)}>
              {t(LegacyMigrationMessages.legacyMigrationStart)}
            </Button>
          </Styled.TenantRow>
        ))}
      </Styled.TenantList>
    </Styled.Card>
  );
};

export default ChooseCustomer;
